import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import {
  ChannelBadge,
  ChannelBlock,
  ChannelColumn,
  DraftDiff,
  DraftGroup,
  DraftRow,
  GoalRow,
  GoalSelectorOptions,
  GoalView,
  GoalViewType,
  TableItem
} from './goal-intake-constants';
import { DATE_FORMAT, snapToMonday } from '../../utils/date-utils';
import { asNames, intersects, validateGoal } from './goal-intake-utils';

const NO_DATE_LABEL = 'No date';

export const toDrafts = (rows: GoalRow[]): DraftRow[] =>
  rows.map(row => ({ key: String(row.id), row }));

const getGroup = (startDate: string | undefined, view: GoalViewType) => {
  if (!startDate) return { key: '', label: NO_DATE_LABEL };
  if (view === GoalView.MONTH) {
    return {
      key: startDate.slice(0, 7),
      label: Dfns.format('MMMM yyyy', Dfns.parseISO(startDate))
    };
  }
  const monday = snapToMonday(startDate);
  return {
    key: monday,
    label: `Week of ${Dfns.format('MMM d, yyyy', Dfns.parseISO(monday))}`
  };
};

// Regroups rows under month or "Week of" (Monday) headers, newest first, rows without a start
// date on top. View-only: it never changes the rows themselves.
export const groupDrafts = (drafts: DraftRow[], view: GoalViewType): DraftGroup[] => {
  const groups = new Map<string, DraftGroup>();
  [...drafts]
    .sort((a, b) => (b.row.start_date ?? '').localeCompare(a.row.start_date ?? ''))
    .forEach(draft => {
      const { key, label } = getGroup(draft.row.start_date, view);
      const group = groups.get(key) ?? { key, label, drafts: [] };
      group.drafts.push(draft);
      groups.set(key, group);
    });
  // The no-date group has the empty key, which a plain descending sort would put last.
  return [...groups.values()].sort((a, b) =>
    (b.key === '' ? '\uffff' : b.key).localeCompare(a.key === '' ? '\uffff' : a.key)
  );
};

// One flat list: a marker row, then that month's/week's rows. Stripes alternate across data rows
// only, so markers neither take a stripe nor shift the parity.
export const toTableItems = (groups: DraftGroup[]): TableItem[] => {
  let dataRowCount = 0;
  return groups.flatMap(group => [
    { key: `marker-${group.key}`, row: {}, markerLabel: group.label },
    ...group.drafts.map(draft => ({ ...draft, isStriped: dataRowCount++ % 2 === 1 }))
  ]);
};

export const diffDrafts = (drafts: DraftRow[], originals: GoalRow[]): DraftDiff => {
  const originalByKey = new Map(originals.map(row => [String(row.id), row]));
  const draftKeys = new Set(drafts.map(draft => draft.key));
  const insert: GoalRow[] = [];
  const update: GoalRow[] = [];
  const changedKeys = new Set<string>();
  drafts.forEach(({ key, row }) => {
    const original = originalByKey.get(key);
    if (!original) {
      insert.push(row);
      changedKeys.add(key);
    } else if (!R.equals(original, row)) {
      update.push(row);
      changedKeys.add(key);
    }
  });
  return {
    insert,
    update,
    delete: originals.filter(row => !draftKeys.has(String(row.id))),
    changedKeys
  };
};

// First validation error per new/edited row, checked against every other row on screen. Saved,
// untouched rows are not re-validated.
export const getRowErrors = (
  drafts: DraftRow[],
  changedKeys: Set<string>,
  customSegments: string[],
  selectorOptions: GoalSelectorOptions
): Map<string, string> => {
  const errors = new Map<string, string>();
  // validateGoal skips the row itself, so every row on screen is passed as is.
  const allRows = drafts.map(draft => draft.row);
  drafts
    .filter(({ key }) => changedKeys.has(key))
    .forEach(({ key, row }) => {
      const { isValid, error } = validateGoal(row, allRows, customSegments, selectorOptions);
      if (!isValid && error) {
        errors.set(key, error);
      }
    });
  return errors;
};

// Legacy bookings only need an amount; a cleared one would otherwise save as a $0 booking.
export const getLegacyRowErrors = (
  drafts: DraftRow[],
  changedKeys: Set<string>
): Map<string, string> =>
  new Map(
    drafts
      .filter(({ key, row }) => changedKeys.has(key) && row.goal_value == null)
      .map(({ key }): [string, string] => [key, 'Goal Value cannot be empty'])
  );

// Mondays from `from` back to `to`'s week, newest first: the rows of the Channel view.
export const getChannelWeeks = (newest: string, oldest: string): string[] => {
  const weeks: string[] = [];
  for (let week = snapToMonday(newest); week >= snapToMonday(oldest);) {
    weeks.push(week);
    week = Dfns.format(DATE_FORMAT, Dfns.subDays(7, Dfns.parseISO(week)));
  }
  return weeks;
};

export const weekEnd = (week: string): string =>
  Dfns.format(DATE_FORMAT, Dfns.addDays(6, Dfns.parseISO(week)));

// Lays one channel's budgets over `weeks` (newest first). Existing bookings are placed first, so
// they win a week over a Goal Intake budget; a budget that cannot get its weeks becomes badges.
export const layoutChannel = (drafts: DraftRow[], weeks: string[]): ChannelColumn => {
  const ordered = R.sortWith(
    [R.descend(d => (d.row.legacy ? 1 : 0)), R.ascend(d => d.row.start_date ?? '')],
    drafts.filter(d => d.row.start_date && d.row.end_date)
  );
  const weekEnds = weeks.map(weekEnd);
  const occupied = new Set<number>();
  const blocks: ChannelBlock[] = [];
  const badges: ChannelBadge[] = [];
  const overlapKeys = new Set<string>();

  ordered.forEach((draft, index) => {
    const { start_date: start = '', end_date: end = '' } = draft.row;
    if (
      ordered.some((other, otherIndex) => otherIndex !== index && intersects(draft.row, other.row))
    ) {
      overlapKeys.add(draft.key);
    }
    const touched = weeks.flatMap((week, row) =>
      week <= end && weekEnds[row] >= start ? [row] : []
    );
    const full = touched.filter(row => start <= weeks[row] && end >= weekEnds[row]);
    if (full.length > 0 && !full.some(row => occupied.has(row))) {
      blocks.push({ draft, startRow: Math.min(...full), span: full.length });
      full.forEach(row => occupied.add(row));
      touched.filter(row => !full.includes(row)).forEach(row => badges.push({ draft, row }));
    } else {
      touched.forEach(row => badges.push({ draft, row }));
      if (full.length > 0) {
        overlapKeys.add(draft.key);
      }
    }
  });
  return { blocks, badges, overlapKeys };
};

// The channels that have a Budget row, sorted; rows without a Channel or dates are not placed.
export const getChannelDrafts = (drafts: DraftRow[]): Map<string, DraftRow[]> => {
  const byChannel = new Map<string, DraftRow[]>();
  drafts.forEach(draft => {
    const { row } = draft;
    if (row.goal_name !== 'Budget' || !row.start_date || !row.end_date) {
      return;
    }
    asNames(row.Channel).forEach(channel => {
      byChannel.set(channel, [...(byChannel.get(channel) ?? []), draft]);
    });
  });
  return new Map([...byChannel.entries()].sort(([a], [b]) => a.localeCompare(b)));
};

// The weeks first shown in the Channel view: `weeksShown` weeks ending at the company's latest budget (at most
// `weeksAhead` past today), so a company whose budgets all ended months ago is not shown empty.
export const getInitialChannelWindow = (
  channelDrafts: Map<string, DraftRow[]>,
  today: string,
  weeksAhead: number,
  weeksShown: number
): { newest: string; oldest: string } => {
  const limit = Dfns.format(DATE_FORMAT, Dfns.addWeeks(weeksAhead, Dfns.parseISO(today)));
  const latest = [...channelDrafts.values()]
    .flat()
    .reduce((max, { row }) => ((row.end_date ?? '') > max ? (row.end_date ?? '') : max), '');
  const newest = latest && latest < limit ? latest : limit;
  return {
    newest,
    oldest: Dfns.format(DATE_FORMAT, Dfns.subWeeks(weeksShown, Dfns.parseISO(newest)))
  };
};

// Every week the Channel view scrolls over: from the earliest budget (or `weeksShown` weeks back with no data)
// up to the later of the opening week and `weeksAhead` past today. `open` is the week to scroll to.
export const getChannelRange = (
  channelDrafts: Map<string, DraftRow[]>,
  today: string,
  weeksAhead: number,
  weeksShown: number
): { newest: string; oldest: string; open: string } => {
  const { newest: open, oldest: yearBack } = getInitialChannelWindow(
    channelDrafts,
    today,
    weeksAhead,
    weeksShown
  );
  const earliest = [...channelDrafts.values()]
    .flat()
    .reduce(
      (min, { row }) => (row.start_date && row.start_date < min ? row.start_date : min),
      yearBack
    );
  const ahead = Dfns.format(DATE_FORMAT, Dfns.addWeeks(weeksAhead, Dfns.parseISO(today)));
  return { newest: open > ahead ? open : ahead, oldest: earliest, open };
};
